import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../../../lib/api';

export interface ManagedSetupData {
  subdomain: string;
  url: string;
  customDomain: string | null;
}

interface Props {
  tenantId: string;
  onStepComplete: (data: ManagedSetupData) => void;
  savedData?: ManagedSetupData;
}

interface SetupResponse {
  subdomain: string | null;
  url: string | null;
  customDomain: string | null;
  cnameTarget: string | null;
}

type SubdomainStatus = 'idle' | 'checking' | 'available' | 'taken';

const BASE_DOMAIN = 'app.blueprintsdlc.com';

export default function ManagedSetupStep({ tenantId, onStepComplete, savedData }: Props) {
  const [subdomain, setSubdomain] = useState(savedData?.subdomain ?? '');
  const [customDomain, setCustomDomain] = useState(savedData?.customDomain ?? '');
  const [status, setStatus] = useState<SubdomainStatus>('idle');
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading] = useState(!savedData);
  const [error, setError] = useState('');
  const [domainWarning, setDomainWarning] = useState('');
  const [copied, setCopied] = useState<'url' | 'cname' | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialised = useRef(false);

  // Auto-provision on mount
  useEffect(() => {
    if (initialised.current || savedData) return;
    initialised.current = true;

    apiFetch<SetupResponse>(`/tenants/${tenantId}/managed-setup`, { method: 'POST' })
      .then(res => {
        if (res.subdomain) {
          setSubdomain(res.subdomain);
          setStatus('available');
        }
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to generate subdomain'))
      .finally(() => setLoading(false));
  }, [tenantId, savedData]);

  const validateSubdomain = useCallback((value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length < 3) {
      setStatus('idle');
      return;
    }

    setStatus('checking');
    debounceRef.current = setTimeout(() => {
      apiFetch<SetupResponse>(`/tenants/${tenantId}/managed-setup`, {
        method: 'PATCH',
        body: JSON.stringify({ subdomain: value }),
      })
        .then(() => {
          setStatus('available');
          setSuggestion('');
        })
        .catch(async (err) => {
          // Parse 409 suggestion from error
          if (err instanceof Error && err.message === 'Subdomain already taken') {
            setStatus('taken');
            // Try to get suggestion by re-fetching
            setSuggestion(value + '-2');
          } else {
            setStatus('taken');
            setSuggestion('');
          }
        });
    }, 500);
  }, [tenantId]);

  const handleSubdomainChange = (value: string) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomain(cleaned);
    validateSubdomain(cleaned);
  };

  const handleCopy = (text: string, type: 'url' | 'cname') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const handleContinue = async () => {
    setDomainWarning('');
    const trimmedDomain = customDomain.trim();
    // Best-effort: customDomain PATCH is conditional on a non-empty value.
    // If the PATCH fails, surface a non-blocking warning but still call
    // onStepComplete (mirrors BYOCCredentialsStep contract).
    if (trimmedDomain) {
      try {
        await apiFetch(`/tenants/${tenantId}/managed-setup`, {
          method: 'PATCH',
          body: JSON.stringify({ customDomain: trimmedDomain }),
        });
      } catch {
        setDomainWarning('Failed to set custom domain — set it later in Settings → Organisation.');
      }
    }
    onStepComplete({
      subdomain,
      url: `https://${subdomain}.${BASE_DOMAIN}`,
      customDomain: trimmedDomain || null,
    });
  };

  const isValid = subdomain.length >= 3 && status === 'available';
  const fullUrl = `https://${subdomain}.${BASE_DOMAIN}`;
  const cnameTarget = `${subdomain}.${BASE_DOMAIN}`;

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>
        Generating your application URL...
      </div>
    );
  }

  return (
    <div>
      <h3 style={{
        fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700,
        color: 'var(--text)', marginBottom: 4,
      }}>
        Your application URL
      </h3>
      <p style={{
        fontFamily: 'var(--font-body)', fontSize: 14,
        color: 'var(--text-secondary)', marginBottom: 28,
      }}>
        Your app will be live at:
      </p>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 16, borderRadius: 'var(--radius-sm)',
          background: 'rgba(239,68,68,0.08)', color: '#dc2626',
          fontFamily: 'var(--font-body)', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* URL display */}
      {subdomain && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 20px', marginBottom: 24,
          background: 'rgba(124,58,237,0.04)',
          border: '1px solid rgba(124,58,237,0.15)',
          borderRadius: 'var(--radius-lg)',
        }}>
          <span style={{
            fontFamily: 'var(--font-mono, monospace)', fontSize: 18, fontWeight: 700,
            color: 'var(--primary)', wordBreak: 'break-all',
          }}>
            {fullUrl}
          </span>
          <button
            type="button"
            onClick={() => handleCopy(fullUrl, 'url')}
            style={{
              flexShrink: 0, padding: '6px 14px',
              fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600,
              background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
            }}
          >
            {copied === 'url' ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}

      {/* Subdomain input */}
      <div style={{ marginBottom: 24 }}>
        <label style={{
          display: 'block', fontFamily: 'var(--font-body)', fontSize: 13,
          fontWeight: 600, color: 'var(--text)', marginBottom: 6,
        }}>
          Subdomain
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="text"
            value={subdomain}
            onChange={e => handleSubdomainChange(e.target.value)}
            maxLength={20}
            placeholder="your-app"
            style={{
              fontFamily: 'var(--font-body)', fontSize: 14,
              padding: '9px 12px', width: 240,
              border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
              background: 'var(--bg)', color: 'var(--text)',
              outline: 'none',
            }}
            data-testid="subdomain-input"
          />
          <span style={{
            fontFamily: 'var(--font-body)', fontSize: 13,
            color: 'var(--text-secondary)',
          }}>
            .{BASE_DOMAIN}
          </span>
        </div>
        <div style={{ marginTop: 6, fontFamily: 'var(--font-body)', fontSize: 12, minHeight: 18 }}>
          {status === 'checking' && (
            <span style={{ color: 'var(--text-secondary)' }}>Checking...</span>
          )}
          {status === 'available' && subdomain.length >= 3 && (
            <span style={{ color: '#059669' }} data-testid="status-available">Available</span>
          )}
          {status === 'taken' && (
            <span style={{ color: '#dc2626' }} data-testid="status-taken">
              Already taken{suggestion ? ` \u2014 try ${suggestion}` : ''}
            </span>
          )}
        </div>
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: 11,
          color: 'var(--text-secondary)', marginTop: 4,
        }}>
          3-20 characters, lowercase letters, numbers, and hyphens only
        </p>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--border)', marginBottom: 24 }} />

      {/* Custom domain */}
      <div style={{ marginBottom: 24 }}>
        <label style={{
          display: 'block', fontFamily: 'var(--font-body)', fontSize: 13,
          fontWeight: 600, color: 'var(--text)', marginBottom: 6,
        }}>
          Custom domain (optional)
        </label>
        <input
          type="text"
          value={customDomain}
          onChange={e => setCustomDomain(e.target.value)}
          placeholder="myapp.com"
          style={{
            fontFamily: 'var(--font-body)', fontSize: 14,
            padding: '9px 12px', width: 320,
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            background: 'var(--bg)', color: 'var(--text)',
            outline: 'none',
          }}
          data-testid="custom-domain-input"
        />

        {/* CNAME instructions */}
        {customDomain.trim() && subdomain && (
          <div style={{
            marginTop: 16, padding: 16,
            background: 'rgba(0,0,0,0.02)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
          }}>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
              color: 'var(--text)', marginBottom: 12,
            }}>
              Add this CNAME to your DNS provider:
            </p>
            <div style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px',
              fontFamily: 'var(--font-body)', fontSize: 13,
            }}>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Type:</span>
              <span style={{ color: 'var(--text)' }}>CNAME</span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Name:</span>
              <span style={{ color: 'var(--text)' }}>@ or www</span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>Value:</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <code style={{
                  fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
                  padding: '2px 6px', background: 'rgba(0,0,0,0.04)',
                  borderRadius: 'var(--radius-sm)', color: 'var(--text)',
                }}>
                  {cnameTarget}
                </code>
                <button
                  type="button"
                  onClick={() => handleCopy(cnameTarget, 'cname')}
                  style={{
                    padding: '3px 10px',
                    fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600,
                    background: 'transparent', color: 'var(--primary)',
                    border: '1px solid var(--primary)', borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                  }}
                >
                  {copied === 'cname' ? 'Copied!' : 'Copy'}
                </button>
              </span>
            </div>
            <p style={{
              fontFamily: 'var(--font-body)', fontSize: 11,
              color: 'var(--text-secondary)', marginTop: 12, marginBottom: 0,
            }}>
              DNS changes can take up to 48 hours to propagate.
            </p>
          </div>
        )}
      </div>

      {/* Warning banner — customDomain PATCH failed */}
      {domainWarning && (
        <div style={{
          marginBottom: 16, padding: '10px 12px',
          background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--warning)',
        }}>
          {domainWarning}
        </div>
      )}

      {/* Continue button */}
      <button
        type="button"
        onClick={() => void handleContinue()}
        disabled={!isValid}
        style={{
          fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
          padding: '10px 24px',
          background: isValid ? 'var(--primary)' : 'var(--border)',
          color: isValid ? '#fff' : 'var(--text-secondary)',
          border: 'none', borderRadius: 'var(--radius-sm)',
          cursor: isValid ? 'pointer' : 'not-allowed',
          opacity: isValid ? 1 : 0.6,
        }}
        data-testid="continue-btn"
      >
        Continue &rarr;
      </button>
    </div>
  );
}
